import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";

import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import {
  listQuickWords,
  upsertQuickWord,
  deleteQuickWord,
  publishQuickWord,
} from "./quick-word-service";
import QuickWordConfigDialog from "./QuickWordConfigDialog";
import QuickWordDeleteDialog from "./QuickWordDeleteDialog";
import QuickWordPublishDialog from "./QuickWordPublishDialog";

const EllipsisTooltipCell = ({ value, width = 150 }) => (
  <Tooltip title={value} placement="top-start">
    <Typography
      variant="body2"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: width,
        display: "block",
      }}
    >
      {value}
    </Typography>
  </Tooltip>
);

function QuickWordView() {
  const { apiCall } = useApiCall();
  const { successToast, errorToast } = useToast();

  const [commands, setCommands] = useState([]);
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("prod");

  const [selectedCommand, setSelectedCommand] = useState(null);
  const [formData, setFormData] = useState(null);
  const [jsonContent, setJsonContent] = useState("");
  const [jsonError, setJsonError] = useState("");

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const [isProcessing, setIsProcessing] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const defaultCommand = {
    id: "",
    version: "stg",
    provider: "",
    keyword: "",
    items: [
      {
        quickWord: "",
        regex: "",
        vars: [],
      },
    ],
    varTypes: [
      {
        varName: "",
        varType: "",
      },
    ],
    descriptions: [
      {
        lang: "EN",
        description: "",
      },
    ],
    script: "",
  };

  const baseCommandSchema = {
    type: "object",
    properties: {
      id: {
        type: "string",
        title: "Command ID",
        description: "Unique identifier for the quick command",
      },
      version: {
        type: "string",
        title: "Version",
        enum: ["stg", "prod"],
        default: "stg",
        description: "Command version (auto-set by service)",
      },
      provider: {
        type: "string",
        title: "Provider",
        description: "Service provider for this command (auto-set by service)",
      },
      keyword: {
        type: "string",
        title: "Keyword",
        description: "Primary keyword that triggers this command",
      },
      items: {
        type: "array",
        title: "Quick Word Items",
        description: "Define quick word patterns with regex and variables",
        items: {
          type: "object",
          title: false,
          properties: {
            quickWord: {
              type: "string",
              title: "Quick Word",
              description: "Quick word pattern for command matching",
            },
            regex: {
              type: "string",
              title: "Regex Pattern",
              description:
                "Regular expression pattern (auto-generated by service)",
            },
            vars: {
              type: "array",
              title: "Variables",
              description:
                "Variable names extracted from this pattern (auto-generated by service)",
              items: {
                type: "string",
              },
            },
          },
        },
      },
      varTypes: {
        type: "array",
        title: "Variable Types",
        description: "Define variable types for the extracted variables",
        items: {
          type: "object",
          title: false,
          properties: {
            varName: {
              type: "string",
              title: "Variable Name",
              description: "Name of the variable",
            },
            varType: {
              type: "string",
              title: "Variable Type",
              description: "Type of the variable (e.g., string, number, date)",
            },
          },
        },
      },
      descriptions: {
        type: "array",
        title: "Descriptions",
        description: "Descriptions of the command in different languages",
        items: {
          type: "object",
          title: false,
          properties: {
            lang: {
              type: "string",
              title: "Language",
              enum: [
                "EN",
                "ZH",
                "ES",
                "HI",
                "AR",
                "PT",
                "BN",
                "RU",
                "JA",
                "FR",
                "DE",
              ],
              enumNames: [
                "English (EN)",
                "Chinese (ZH)",
                "Spanish (ES)",
                "Hindi (HI)",
                "Arabic (AR)",
                "Portuguese (PT)",
                "Bengali (BN)",
                "Russian (RU)",
                "Japanese (JA)",
                "French (FR)",
                "German (DE)",
              ],
            },
            description: {
              type: "string",
              title: "Description",
            },
          },
          required: ["lang", "description"],
        },
        minItems: 1,
      },
      script: {
        type: "string",
        title: "Execute Script",
        description: "Script to execute when the command matches",
      },
    },
  };

  const baseUiSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    keyword: {
      "ui:placeholder": "Enter primary keyword",
      "ui:description": "Primary keyword that triggers this command",
    },
    items: {
      "ui:addButtonLabel": "Add Quick Word Item",
      "ui:description":
        "Add multiple quick word items with patterns and variables",
      items: {
        "ui:title": false,
        quickWord: {
          "ui:widget": "textarea",
          "ui:options": {
            rows: 3,
          },
          "ui:placeholder": "Enter quick word pattern",
          "ui:description":
            "Quick word pattern for matching (regex and vars will be auto-generated)",
        },
        regex: {
          "ui:widget": "textarea",
          "ui:options": {
            rows: 2,
          },
          "ui:placeholder": "Auto-generated regex pattern",
          "ui:description":
            "Regular expression pattern (auto-generated by service)",
          "ui:readonly": true,
          "ui:disabled": true,
        },
        vars: {
          "ui:addButtonLabel": "Add Variable",
          "ui:placeholder": "Enter variable name",
          "ui:description":
            "Variable names that will be extracted from the pattern (auto-generated by service)",
          "ui:readonly": true,
          "ui:disabled": true,
        },
      },
    },
    varTypes: {
      "ui:addButtonLabel": "Add Variable Type",
      "ui:description":
        "Define types for variables that will be extracted from patterns",
      items: {
        "ui:title": false,
        varName: {
          "ui:placeholder": "Enter variable name",
          "ui:description":
            "Name of the variable (should match extracted variables)",
        },
        varType: {
          "ui:placeholder": "Enter variable type",
          "ui:description":
            "Type of the variable (e.g., string, number, date, boolean)",
        },
      },
    },
    descriptions: {
      "ui:addButtonLabel": "Add Language Description",
      "ui:description":
        "Add descriptions for the command in different languages. At least one description is required.",
      "ui:title": "Multi-language Descriptions",
      items: {
        "ui:title": false,
        "ui:order": ["lang", "description"],
        lang: {
          "ui:widget": "select",
          "ui:placeholder": "Select language",
          "ui:description": "Choose the language for this description",
        },
        description: {
          "ui:widget": "textarea",
          "ui:options": {
            rows: 3,
          },
          "ui:placeholder": "Enter description in the selected language",
          "ui:description":
            "Description of the command in the selected language",
        },
      },
    },
  };

  const baseUiSchemaWithScriptEditor = useMemo(
    () => ({
      ...baseUiSchema,
      script: {
        "ui:widget": "CodeEditorWidget",
        "ui:description":
          "Script to execute when the command matches (supports YAML/JavaScript syntax)",
      },
    }),
    [],
  );

  const createUiSchema = useMemo(
    () => ({
      ...baseUiSchemaWithScriptEditor,
      version: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Version will be auto-set by service",
      },
      provider: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Provider will be auto-set by service",
      },
    }),
    [baseUiSchemaWithScriptEditor],
  );

  const editUiSchema = useMemo(
    () => ({
      ...baseUiSchemaWithScriptEditor,
      id: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "ID cannot be modified",
      },
      version: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Version cannot be modified",
      },
      provider: {
        "ui:readonly": true,
        "ui:disabled": true,
        "ui:description": "Provider cannot be modified",
      },
    }),
    [baseUiSchemaWithScriptEditor],
  );

  // Transform functions for varTypes between array (form) and object (backend)
  const transformVarTypesToArray = varTypesObj => {
    if (!varTypesObj || typeof varTypesObj !== "object") return [];
    return Object.entries(varTypesObj).map(([varName, varType]) => ({
      varName,
      varType,
    }));
  };

  const transformVarTypesToObject = varTypesArray => {
    if (!Array.isArray(varTypesArray)) return {};
    const result = {};
    varTypesArray.forEach(({ varName, varType }) => {
      if (varName && varType) {
        result[varName] = varType;
      }
    });
    return result;
  };

  // Transform command data for form display
  const transformCommandForForm = command => {
    if (!command) return null;
    return {
      ...command,
      varTypes: transformVarTypesToArray(command.varTypes),
    };
  };

  // Transform form data for backend
  const transformFormDataForBackend = formData => {
    if (!formData) return null;
    return {
      ...formData,
      varTypes: transformVarTypesToObject(formData.varTypes),
    };
  };

  useEffect(() => {
    fetchCommands();
  }, []);

  useEffect(() => {
    if (commands.length > 0) {
      filterCommands();
    }
  }, [searchTerm, activeTab, commands]);

  useEffect(() => {
    if (!configDialogOpen && !deleteDialogOpen && !publishDialogOpen) {
      setIsProcessing(null);
    }
  }, [configDialogOpen, deleteDialogOpen, publishDialogOpen]);

  const fetchCommands = async () => {
    setLoading(true);
    try {
      const resp = await listQuickWords(apiCall);
      if (resp.schemas) {
        setCommands(resp.schemas);
        filterCommands(resp.schemas);
      }
    } catch (error) {
      errorToast("Failed to load commands");
      console.error("Failed to load commands:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterCommands = (data = commands) => {
    let filtered = data.filter(cmd => cmd.version === activeTab);

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(cmd => {
        const id = cmd.id.toLowerCase();
        const keyword = cmd.keyword ? cmd.keyword.toLowerCase() : "";

        return id.includes(query) || keyword.includes(query);
      });
    }

    setFilteredCommands(filtered);
  };
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleCreateNew = () => {
    setIsProcessing("create");
    setTimeout(() => {
      const formCommand = transformCommandForForm(defaultCommand);
      setSelectedCommand(defaultCommand);
      setFormData(formCommand);
      setJsonContent(JSON.stringify(defaultCommand, null, 2));
      setConfigDialogOpen(true);
      setIsProcessing(null);
    }, 500);
  };

  const handleEditCommand = command => {
    const formCommand = transformCommandForForm(command);
    setSelectedCommand(command);
    setFormData(formCommand);
    setJsonContent(JSON.stringify(command, null, 2));
    setConfigDialogOpen(true);
  };

  const handleDeleteClick = command => {
    setSelectedCommand(command);
    setDeleteDialogOpen(true);
  };

  const handlePublishClick = command => {
    setSelectedCommand(command);
    setPublishDialogOpen(true);
  };

  const handleCloseConfigDialog = () => {
    setConfigDialogOpen(false);
    setSelectedCommand(null);
    setFormData(null);
    setJsonContent("");
    setJsonError("");
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCommand(null);
  };

  const handleClosePublishDialog = () => {
    setPublishDialogOpen(false);
    setSelectedCommand(null);
  };

  const handleFormChange = ({ formData: newFormData }) => {
    setFormData(newFormData);
    // Update JSON content when form changes - transform to backend format for JSON display
    if (newFormData) {
      const backendData = transformFormDataForBackend(newFormData);
      setJsonContent(JSON.stringify(backendData, null, 2));
      setJsonError(""); // Clear any JSON errors since form is valid
    }
  };

  const handleJsonChange = useCallback(instance => {
    const jsonText = instance.getValue();
    setJsonContent(jsonText);

    // Try to parse and update form data
    try {
      const parsedData = JSON.parse(jsonText);
      const formData = transformCommandForForm(parsedData);
      setFormData(formData);
      setJsonError(""); // Clear any previous errors
    } catch (error) {
      // Invalid JSON, don't update form data
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  }, []);

  const handleSaveCommand = async () => {
    if (!formData) return;

    setIsProcessing("save");
    try {
      const response = await upsertQuickWord(
        apiCall,
        transformFormDataForBackend(formData),
      );

      if (response && response.success) {
        successToast("Command saved successfully");

        await fetchCommands();
      } else {
        errorToast(response?.message || "Failed to save command");
      }

      handleCloseConfigDialog();
    } catch (error) {
      errorToast("Failed to save command");
      console.error("Failed to save command:", error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDeleteCommand = async () => {
    if (!selectedCommand) return;

    try {
      const { id, version } = selectedCommand;
      setIsProcessing("delete");

      const response = await deleteQuickWord(apiCall, id, version);

      if (response && response.success) {
        successToast("Command deleted successfully");

        await fetchCommands();
      } else {
        errorToast(response?.message || "Failed to delete command");
      }

      handleCloseDeleteDialog();
    } catch (error) {
      errorToast("Failed to delete command");
      console.error("Failed to delete command:", error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handlePublishCommand = async () => {
    if (!selectedCommand) return;

    setIsProcessing("publish");
    try {
      const { id } = selectedCommand;
      setIsProcessing("publish");

      const response = await publishQuickWord(apiCall, id);

      if (response && response.success) {
        successToast("Command published successfully");

        await fetchCommands();
      } else {
        errorToast(response?.message || "Failed to publish command");
      }

      handleClosePublishDialog();
    } catch (error) {
      errorToast("Failed to publish command");
      console.error("Failed to publish command:", error);
    } finally {
      setIsProcessing(null);
    }
  };
  const handleRefreshCommands = async () => {
    setIsProcessing("refresh");
    try {
      await fetchCommands();
      successToast("Commands refreshed");
    } catch (error) {
      errorToast("Failed to refresh commands");
    } finally {
      setIsProcessing(null);
    }
  };

  // const handleReloadCommands = async () => {
  //   setIsProcessing('reload');
  //   try {
  //     await reloadCommands(apiCall);
  //     successToast('Commands reloaded in the system');
  //   } catch (error) {
  //     errorToast('Failed to reload commands');
  //     console.error('Failed to reload commands:', error);
  //   } finally {
  //     setIsProcessing(null);
  //   }
  // };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  const columns = [
    {
      field: "id",
      headerName: "Command ID",
      flex: 1,
      renderCell: params => (
        <EllipsisTooltipCell value={params.value} width={200} />
      ),
    },
    {
      field: "keyword",
      headerName: "Keyword",
      flex: 1,
      renderCell: params => (
        <EllipsisTooltipCell value={params.value} width={200} />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      renderCell: params => {
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 1,
              width: "100%",
              height: "100%",
              overflow: "hidden",
              overflowY: "hidden",
              whiteSpace: "nowrap",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              position: "relative",
              "&:hover": {
                overflow: "auto",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: 0,
                height: "100%",
                width: "24px",
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
                pointerEvents: "none",
              },
              "&:hover::after": {
                display: "none",
              },
            }}
            onClick={e => e.stopPropagation()}
          >
            {params.row.version === "stg" && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handlePublishClick(params.row.original);
                }}
                sx={{ flexShrink: 0 }}
              >
                Publish
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick(params.row.original);
              }}
              sx={{ flexShrink: 0 }}
            >
              Delete
            </Button>
          </Box>
        );
      },
    },
  ];

  const rows = filteredCommands.map(cmd => ({
    id: cmd.id,
    version: cmd.version,
    keyword: cmd.keyword,
    original: cmd,
  }));
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        Quick Command Manager
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{
            style: { transition: "none" },
          }}
          sx={{
            "& .MuiTab-root": {
              width: "calc(50% - 8px)",
              mx: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          <Tab value="prod" label="Production Commands" />
          <Tab value="stg" label="Stg Commands" />
        </Tabs>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap" },
            gap: 1,
            mb: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleRefreshCommands}
            disabled={isProcessing === "refresh"}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
              height: 36,
              minWidth: "fit-content",
            }}
          >
            Refresh
            {isProcessing === "refresh" && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Button>

          {/* <Button
            variant="outlined"
            onClick={handleReloadCommands}
            disabled={isProcessing === 'reload'}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
              height: 36,
              minWidth: 'max-content'
            }}
          >
            Reload System
            {isProcessing === 'reload' && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Button> */}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            disabled={isProcessing === "create"}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
              height: 36,
              minWidth: "max-content",
            }}
          >
            Create Command
            {isProcessing === "create" && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
          </Button>
        </Box>
        <TextField
          placeholder="Search commands..."
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, width: "100%", mt: 2, overflow: "hidden" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            disableColumnMenu
            onRowClick={params => handleEditCommand(params.row.original)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              "& .MuiDataGrid-cell": {
                py: 1,
                display: "flex",
                alignItems: "center",
                borderRight: "none",
              },
              "& .MuiDataGrid-columnHeader": {
                borderRight: "none",
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                {
                  outline: "none",
                },
            }}
          />
        </Box>
      )}

      <QuickWordConfigDialog
        open={configDialogOpen}
        onClose={handleCloseConfigDialog}
        selectedCommand={selectedCommand}
        formData={formData}
        onFormChange={handleFormChange}
        jsonContent={jsonContent}
        onJsonChange={handleJsonChange}
        jsonError={jsonError}
        isProcessing={isProcessing}
        onSave={handleSaveCommand}
        schema={baseCommandSchema}
        createUiSchema={createUiSchema}
        editUiSchema={editUiSchema}
      />

      <QuickWordDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        selectedCommand={selectedCommand}
        onDelete={handleDeleteCommand}
        isProcessing={isProcessing}
      />

      <QuickWordPublishDialog
        open={publishDialogOpen}
        onClose={handleClosePublishDialog}
        selectedCommand={selectedCommand}
        onPublish={handlePublishCommand}
        isProcessing={isProcessing}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default QuickWordView;
